/* eslint-disable array-callback-return */
import { IconButton, TableFooter, useMediaQuery, useTheme } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import DialogContent from '@mui/material/DialogContent'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import { Close } from '@mui/icons-material'
import Box from '@mui/material/Box'
import { CSVLink } from 'react-csv'

import HiddenWholesalerLogo from 'shared/components/HiddenWholesalerLogo'
import { StyledTableCell } from 'shared/components/StyledTableComponents'
import ContainedButton from 'shared/components/Buttons/ContainedButton'
import { useSetActiveCartMutation } from 'shared/store/useSavedCarts'
import getProductName from 'shared/utils/helpers/getProductName'
import ImageComponent from 'shared/components/ImageComponent'
import { Order } from 'shared/store/useOrdersHistory/types'
import CustomDialog from 'shared/components/CustomDialog'
import CustomTable from 'shared/components/CustomTable'
import NonRefundableIcon from 'assets/images/no-return.webp'

import {
   getOrderDetailsService,
   getCsvOrderReportService,
   repeatOrderService,
   markAsCompleteService,
} from '../../services'
import { StoredOrderDetails, ReportType, OrderProduct, AdditionalService } from '../../types'

// Everything which is commented in rating will be used in future
interface Props {
   orderUuids: string[]
   open: boolean
   title: string
   handleOnSearch: () => void
   onClose: () => void
   clientName?: string
   contactName?: string
   details: StoredOrderDetails[]
   updateDetailsStore: (newRecord: StoredOrderDetails, remove?: boolean) => void
   priceType?: PriceType
   orderStatus?: PropType<Order, 'status'>
   orderType?: PropType<Order, 'orderType'>
   formattedNetValue?: string
   formattedGrossValue?: string
   netDeliveryCost?: number
   grossDeliveryCost?: number
   formattedServicesValue?: string
}

export default function OrderDetails({
   orderUuids,
   open,
   handleOnSearch,
   onClose,
   clientName,
   orderStatus,
   details,
   title,
   contactName,
   updateDetailsStore,
   priceType,
   orderType,
   formattedGrossValue,
   formattedNetValue,
   formattedServicesValue,
   netDeliveryCost,
   grossDeliveryCost,
}: Props) {
   // const [rating, setRating] = useState<RatingType>({ conformRate: 0, productionQualityRate: 0, priceToQualityRate: 0 })
   const [isLoading, setIsLoading] = useState(false)
   const [data, setData] = useState<PropType<StoredOrderDetails, 'data'>>({
      products: [],
      additionalServices: [],
   })
   const [isGeneratingCart, setIsGeneratingCart] = useState(false)
   const [isMarkingAsComplete, setIsMarkingAsComplete] = useState(false)
   const { mutate: changeActiveCart } = useSetActiveCartMutation()

   const [reportData, setReportData] = useState<ReportType>()
   const componentRef = useRef(null)
   const { t } = useTranslation(['TRANSLATION', 'ORDERS_HISTORY'])
   const isDetailsStored = useRef(false)
   const theme = useTheme()
   const isMobile = useMediaQuery(theme.breakpoints.down('lg'))
   const navigate = useNavigate()

   // function handleRate(productCartUuid: string, productUuid: string) {
   //    setIsLoading(true)
   //    const payload: RatingPayload = {
   //       conformRate: rating.conformRate,
   //       priceToQualityRate: rating.priceToQualityRate,
   //       productionQualityRate: rating.productionQualityRate,
   //       orderUuid: orderUuids[0],
   //       productCartUuid,
   //       productUuid,
   //    }
   //    addRatingService(payload)
   //       .then(() => updateDetailsStore({ id: orderUuids[0], data }))
   //       .finally(() => setIsLoading(false))
   // }

   async function init() {
      console.log('details', details)
      const storedDetails = details.find((item) => item.id === orderUuids[0])
      if (storedDetails) {
         isDetailsStored.current = true
         setData(storedDetails.data)
      } else {
         setIsLoading(true)
         const req = await getOrderDetailsService(orderUuids)
         if (req) {
            setData(req)
         }
      }
      getCsvOrderReportService(orderUuids).then((res) => {
         setReportData(res)
      })
      setIsLoading(false)
   }

   function getHighlightColor(product: OrderProduct) {
      if (product.status === 'AVAILABLE') {
         return 'transparent'
      }
      // if (product.status === 'UNSET') {
      //    return 'rgb(255, 150, 0,0.7)'
      // }
      if (product.amount !== product.requestedAmount && product.amount !== 0) {
         return 'rgb(255, 136, 0,0.7)'
      }
      return 'rgba(255,0,0,0.5)'
   }

   function generateCSV() {
      const CSVData: (string | number)[][] = []
      const titleRow = [
         t('TRANSLATION:columnNames.partIndex'),
         t('TRANSLATION:columnNames.productName'),
         t('TRANSLATION:columnNames.producer'),
         t('TRANSLATION:columnNames.wholesaler'),
         t('TRANSLATION:columnNames.netPrice'),
         t('TRANSLATION:columnNames.netSum'),
         t('TRANSLATION:columnNames.grossPrice'),
         t('TRANSLATION:columnNames.grossSum'),
         'VAT',
         t('TRANSLATION:columnNames.orderedAmount'),
         t('TRANSLATION:columnNames.notOrderedAmount'),
         t('TRANSLATION:columnNames.status'),
         t('TRANSLATION:columnNames.wholesaleErrorMessage'),
         t('TRANSLATION:columnNames.wholesaleOrderDate'),
         t('TRANSLATION:columnNames.wholesaleTaxId'),
      ]
      CSVData.push(titleRow)
      const services = [t('TRANSLATION:columnNames.services')]
      if (reportData) {
         reportData.orders.map((order) => {
            order.wholesales.map((wholesale) => {
               wholesale.orderedProducts.map((product) => {
                  CSVData.push([
                     product.index,
                     product.name ? `${product.name.split(' ')[0]} ${product.name.split(' ')[1]}` : product.index,
                     product.producerName,
                     wholesale.wholesaleName,
                     product.unitNetPrice,
                     product.netPrice,
                     product.unitGrossPrice,
                     product.grossPrice,
                     product.vat,
                     product.orderedAmount,
                     product.notOrderedAmount,
                     product.orderProductStatus,
                     product.wholesaleErrorMessage || '',
                     wholesale.wholesaleOrderDate ?? '',
                     wholesale.wholesaleTaxId ?? '',
                  ])
               })
            })
            if (order.additionalServices.length > 0) {
               CSVData.push([])
               CSVData.push(services)
               order.additionalServices.map((service) => {
                  CSVData.push([
                     service.name,
                     '',
                     '',
                     service.formattedNetPrice,
                     service.formattedNetValue,
                     service.formattedGrossPrice,
                     service.formattedGrossValue,
                     service.taxRate,
                     service.amount,
                     '',
                     '',
                     '',
                  ])
               })
            }
         })
      }
      return (
         <CSVLink style={{ textAlign: 'right' }} filename={title} data={CSVData}>
            <ContainedButton variant="contained">{t('ORDERS_HISTORY:generateCSV')}</ContainedButton>
         </CSVLink>
      )
   }

   function generateImportCSV() {
      const CSVData: (string | number)[][] = []
      const titleRow = [
         t('TRANSLATION:columnNames.partIndex'),
         t('TRANSLATION:columnNames.productName'),
         t('TRANSLATION:columnNames.producer'),
         t('TRANSLATION:columnNames.wholesaler'),
         t('TRANSLATION:columnNames.netPrice'),
         t('TRANSLATION:columnNames.netSum'),
         t('TRANSLATION:columnNames.grossPrice'),
         t('TRANSLATION:columnNames.grossSum'),
         'VAT',
         t('TRANSLATION:columnNames.orderedAmount'),
         t('TRANSLATION:columnNames.notOrderedAmount'),
         t('TRANSLATION:columnNames.status'),
         t('TRANSLATION:columnNames.wholesaleErrorMessage'),
         t('TRANSLATION:columnNames.wholesaleOrderDate'),
         t('TRANSLATION:columnNames.wholesaleTaxId'),
         t('TRANSLATION:columnNames.priceThreshold'),
         t('TRANSLATION:columnNames.requestedIndex'),
         t('TRANSLATION:columnNames.requestedAmount'),
      ]
      CSVData.push(titleRow)
      const notFoundIndexes = [t('TRANSLATION:columnNames.notFoundIndexes')]
      const rejectedIndexes = [t('TRANSLATION:columnNames.rejectedIndexes')]
      const services = [t('TRANSLATION:columnNames.services')]
      if (reportData) {
         reportData.orders.map((order) => {
            order.wholesales.map((wholesale) => {
               wholesale.orderedProducts.map((product) => {
                  CSVData.push([
                     product.index,
                     product.name ? `${product.name.split(' ')[0]} ${product.name.split(' ')[1]}` : product.index,
                     product.producerName,
                     wholesale.wholesaleName,
                     product.unitNetPrice,
                     product.netPrice,
                     product.unitGrossPrice,
                     product.grossPrice,
                     product.vat,
                     product.orderedAmount,
                     product.notOrderedAmount,
                     product.orderProductStatus,
                     product.wholesaleErrorMessage || '',
                     wholesale.wholesaleOrderDate ?? '',
                     wholesale.wholesaleTaxId ?? '',
                     product.priceThreshold,
                     product.requestedIndex,
                     product.requestedAmount,
                  ])
               })
            })
            if (order.notFoundIndexes.length > 0) {
               CSVData.push([])
               CSVData.push(notFoundIndexes)
               order.notFoundIndexes.map((index) => {
                  CSVData.push(['', index])
               })
            }
            if (order.rejectedIndexes.length > 0) {
               CSVData.push([])
               CSVData.push(rejectedIndexes)
               order.rejectedIndexes.map((index) => {
                  CSVData.push(['', index])
               })
            }
            if (order.additionalServices.length > 0) {
               CSVData.push([])
               CSVData.push(services)
               order.additionalServices.map((service) => {
                  CSVData.push([
                     '',
                     service.name,
                     '',
                     '',
                     service.formattedNetPrice,
                     service.formattedNetValue,
                     service.formattedGrossPrice,
                     service.formattedGrossValue,
                     service.taxRate,
                     service.amount,
                     '',
                     '',
                     '',
                     '',
                  ])
               })
            }
         })
      }

      return (
         <CSVLink style={{ textAlign: 'right' }} filename={title} data={CSVData}>
            <ContainedButton variant="contained">{t('ORDERS_HISTORY:generateCSV')}</ContainedButton>
         </CSVLink>
      )
   }

   useEffect(() => {
      init()
   }, [])

   const handleOnClose = () => {
      if (!isDetailsStored.current && orderType) {
         updateDetailsStore({ orderType, id: orderUuids[0], data })
      }
      onClose()
   }

   return (
      <CustomDialog
         open={open}
         onClose={handleOnClose}
         fullWidth
         scroll="paper"
         maxWidth="xl"
         isLoading={isLoading}
         content={
            <DialogContent ref={componentRef} style={{ minHeight: 300, display: 'flex', flexDirection: 'column' }}>
               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h4" color="primary">
                     {title}
                  </Typography>
                  <IconButton onClick={handleOnClose}>
                     <Close />
                  </IconButton>
               </Box>
               {clientName && (
                  <Typography
                     color="grayText"
                     variant="h6"
                  >{`${t('TRANSLATION:columnNames.client')}: ${clientName}`}</Typography>
               )}
               {contactName && (
                  <Typography
                     color="grayText"
                     variant="h6"
                  >{`${t('TRANSLATION:columnNames.contactPerson')}: ${contactName}`}</Typography>
               )}
               <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', my: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                     <Typography variant="body1" color="grayText">
                        {t('ORDERS_HISTORY:productsTable')}
                     </Typography>
                     <CustomTable
                        dependencies={[]}
                        customRowStyles={(row: OrderProduct) => ({
                           backgroundColor: getHighlightColor(row),
                        })}
                        disablePagination
                        customFooter={(columnsCount) => (
                           <TableFooter sx={{ backgroundColor: '#EBEBEB' }}>
                              {netDeliveryCost ? (
                                 <StyledTableCell colSpan={columnsCount}>
                                    <Box
                                       sx={{
                                          display: 'flex',
                                          gap: '32px',
                                          pr: { lg: '32px', md: '32px' },
                                          width: '100%',
                                          justifyContent: { lg: 'flex-end', md: 'flex-end', xs: 'space-evenly' },
                                       }}
                                    >
                                       <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                          <span>{t('TRANSLATION:deliveryCost')}</span>
                                          <span>
                                             {t('TRANSLATION:price', { price: netDeliveryCost.toFixed(2) })}
                                             {t('TRANSLATION:NET')}
                                          </span>
                                          <span>
                                             {t('TRANSLATION:price', {
                                                price: grossDeliveryCost ? grossDeliveryCost.toFixed(2) : 0,
                                             })}
                                             {t('TRANSLATION:GROSS')}
                                          </span>
                                       </Box>
                                    </Box>
                                 </StyledTableCell>
                              ) : null}
                              <TableRow>
                                 <StyledTableCell colSpan={columnsCount}>
                                    <Box
                                       sx={{
                                          display: 'flex',
                                          gap: '32px',
                                          pr: { lg: '24px', md: '72px', xs: '24px' },
                                          width: '100%',
                                          justifyContent: 'flex-end',
                                       }}
                                    >
                                       {priceType === 'NET' ? (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                             <p style={{ fontWeight: 'bold', fontSize: 13 }}>
                                                {t('TRANSLATION:columnNames.netSum')}
                                             </p>
                                             <p style={{ fontWeight: 'bold', fontSize: 13, color: 'black' }}>
                                                {formattedNetValue}
                                             </p>
                                          </Box>
                                       ) : (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                             <p style={{ fontWeight: 'bold', fontSize: 13 }}>
                                                {t('TRANSLATION:columnNames.grossSum')}
                                             </p>
                                             <p style={{ fontWeight: 'bold', fontSize: 13, color: 'black' }}>
                                                {formattedGrossValue}
                                             </p>
                                          </Box>
                                       )}
                                    </Box>
                                 </StyledTableCell>
                              </TableRow>
                           </TableFooter>
                        )}
                        rows={data.products}
                        dataSize={data.products.length}
                        columns={[
                           {
                              name: 'productName',
                              renderCell: (row: OrderProduct) => getProductName(row),
                              mainOnMobile: true,
                           },
                           {
                              name: 'orderNumber',
                              renderCell: (row: OrderProduct) => row.wholesaleOrderNumber,
                           },
                           {
                              name: 'producer',
                              renderCell: (row: OrderProduct) => (
                                 <Tooltip title={row.producerName}>
                                    <ImageComponent
                                       producerUuid={row.producerUuid}
                                       height={isMobile ? '15px' : '20px'}
                                       filename={row.producerNameWithoutSpecialChars.toLowerCase()}
                                       producerName={row.producerName}
                                    />
                                 </Tooltip>
                              ),
                           },
                           {
                              name: 'partIndex',
                              renderCell: (row: OrderProduct) => row.index,
                           },
                           {
                              name: 'quantity',
                              renderCell: (row: OrderProduct) => (
                                 <Tooltip
                                    disableHoverListener={row.amount === 0}
                                    title={t('TRANSLATION:requestedAmountTooltip', {
                                       amount: row.amount,
                                       requestedAmount: row.requestedAmount,
                                    })}
                                 >
                                    <Box>{row.requestedAmount}</Box>
                                 </Tooltip>
                              ),
                           },
                           {
                              name: isMobile ? 'wholesalerAndNumber' : 'wholesaler',
                              mainOnMobile: true,
                              renderCell: (row: OrderProduct) =>
                                 isMobile ? (
                                    <Box
                                       sx={{
                                          display: 'flex',
                                          flexDirection: 'column',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                       }}
                                    >
                                       <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                          <HiddenWholesalerLogo
                                             wholesalerName={row.wholesaleName}
                                             wholesalerUuid={row.wholesaleUuid}
                                             imgWidth="auto"
                                             imgHeight="15px"
                                          />
                                          {row.wholesaleParams && row.wholesaleParams.nonRefundable && (
                                             <Tooltip title={t('TRANSLATION:nonRefundable')}>
                                                <img
                                                   height="25px"
                                                   width="auto"
                                                   src={NonRefundableIcon}
                                                   alt="non-refundable-icon"
                                                />
                                             </Tooltip>
                                          )}
                                       </Box>
                                       {row.wholesaleOrderNumber}
                                    </Box>
                                 ) : (
                                    <Box
                                       sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center' }}
                                    >
                                       <HiddenWholesalerLogo
                                          wholesalerName={row.wholesaleName}
                                          wholesalerUuid={row.wholesaleUuid}
                                          imgWidth="auto"
                                          imgHeight="20px"
                                       />
                                       {row.wholesaleParams && row.wholesaleParams.nonRefundable && (
                                          <Tooltip title={t('TRANSLATION:nonRefundable')}>
                                             <img
                                                height="25px"
                                                width="auto"
                                                src={NonRefundableIcon}
                                                alt="non-refundable-icon"
                                             />
                                          </Tooltip>
                                       )}
                                    </Box>
                                 ),
                           },
                           {
                              name: 'userName',
                              hidden: orderType !== 'GROUPED_BUY_NOW',
                              renderCell: (row: OrderProduct) => row.username || '',
                           },
                           {
                              name: 'orderTime',
                              hidden: orderType !== 'GROUPED_BUY_NOW',
                              renderCell: (row: OrderProduct) => row.orderDate || '',
                           },
                           {
                              name: 'wholesaleErrorMessage',
                              hidden: !data.products.some((product) => product.status !== 'AVAILABLE'),
                              renderCell: (row: OrderProduct) => row.wholesaleErrorMessage ?? '',
                           },

                           {
                              name: 'clientPrice',
                              customLabel: `${t('TRANSLATION:columnNames.clientPrice')} ${t(`TRANSLATION:${priceType}`)}`,
                              hidden: orderType === 'BASELINKER',
                              renderCell: (row: OrderProduct) =>
                                 priceType === 'NET' ? row.formattedClientNetPrice : row.formattedClientGrossPrice,
                           },
                           {
                              name: 'price',
                              customLabel: `${t('TRANSLATION:columnNames.price')} ${t(`TRANSLATION:${priceType}`)}`,
                              mainOnMobile: true,
                              renderCell: (row: OrderProduct) => (
                                 <>
                                    {priceType === 'NET' ? row.formattedPrice : row.formattedGrossPrice}
                                    {typeof row.formattedConvertedPrice !== 'undefined' &&
                                       (priceType === 'NET'
                                          ? ` (${row.formattedConvertedPrice})`
                                          : ` (${row.formattedConvertedGrossPrice})`)}
                                 </>
                              ),
                           },
                        ]}
                     />
                  </Box>
                  {data.additionalServices.length > 0 && (
                     <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <Typography variant="body1" color="grayText">
                           {t('ORDERS_HISTORY:servicesTable')}
                        </Typography>
                        <CustomTable
                           dependencies={[]}
                           disablePagination
                           customFooter={(columnsCount) => (
                              <TableFooter sx={{ backgroundColor: '#EBEBEB' }}>
                                 <StyledTableCell colSpan={columnsCount}>
                                    <Box
                                       sx={{
                                          display: 'flex',
                                          gap: '32px',
                                          pr: { lg: '32px', md: '32px' },
                                          width: '100%',
                                          justifyContent: { lg: 'flex-end', md: 'flex-end', xs: 'space-evenly' },
                                       }}
                                    >
                                       <p style={{ fontWeight: 'bold', fontSize: 13 }}>
                                          {`${t('TRANSLATION:columnNames.servicesSum')}:`}
                                       </p>
                                       <p style={{ fontWeight: 'bold', fontSize: 13, color: 'black' }}>
                                          {formattedServicesValue}
                                       </p>
                                    </Box>
                                 </StyledTableCell>
                              </TableFooter>
                           )}
                           rows={data.additionalServices}
                           dataSize={data.additionalServices.length}
                           columns={[
                              {
                                 name: 'serviceName',
                                 renderCell: (row: AdditionalService) => row.name,
                                 mainOnMobile: true,
                              },
                              {
                                 name: 'quantity',
                                 renderCell: (row: AdditionalService) => row.amount,
                              },
                              {
                                 name: 'price',
                                 renderCell: (row: AdditionalService) =>
                                    priceType === 'NET' ? row.formattedNetPrice : row.formattedGrossPrice,
                              },
                           ]}
                        />
                     </Box>
                  )}
               </Box>
               {!isMobile && (
                  <Box sx={{ display: 'flex', gap: '8px', width: '100%', justifyContent: 'flex-end' }}>
                     {/* {orderType === 'EXTENSION' && (orderStatus === 'UNSET' || !orderStatus) && (
                        <ContainedButton
                           disabled={isMarkingAsComplete}
                           onClick={(e) => {
                              setIsMarkingAsComplete(true)
                              e.preventDefault()
                              e.stopPropagation()
                              markAsCompleteService(orderUuids[0])
                                 .then(() => {
                                    updateDetailsStore({ orderType, id: orderUuids[0], data }, true)
                                    handleOnSearch()
                                    onClose()
                                 })
                                 .finally(() => setIsMarkingAsComplete(false))
                           }}
                           variant="contained"
                        >
                           {t('ORDERS_HISTORY:confirmOrder')}
                        </ContainedButton>
                     )} */}
                     {orderType !== 'GROUPED_BUY_NOW' && (
                        <ContainedButton
                           disabled={isGeneratingCart}
                           onClick={(e) => {
                              setIsGeneratingCart(true)
                              e.preventDefault()
                              e.stopPropagation()
                              repeatOrderService(orderUuids[0])
                                 .then((res) => {
                                    changeActiveCart(res.cartDetailsDTO.cartUuid, {
                                       onSuccess: () => {
                                          navigate('/dashboard/main-cart?orderAgain=true')
                                       },
                                       onSettled: () => {
                                          setIsGeneratingCart(false)
                                       },
                                    })
                                 })
                                 .finally(() => setIsGeneratingCart(false))
                           }}
                           variant="contained"
                        >
                           {t('ORDERS_HISTORY:orderAgain')}
                        </ContainedButton>
                     )}
                     {orderType === 'CART_CSV' ? generateImportCSV() : generateCSV()}
                  </Box>
               )}
            </DialogContent>
         }
      />
   )
}
